import { canOpenRoute, findSidebarRow, searchProducts } from "./searchAccess";

// Fallback until the backend registry lists every page: the sidebar pages
// (routes.js) the user can open, matched in the browser and added after
// the server's page results. Server results always come first; a page the
// server already returned is not added twice. When the server's registry is
// complete this adds nothing.

const normalize = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

export const routeKey = (route) => {
  const [path, query = ""] = String(route || "").split("?");
  const params = new URLSearchParams(query);
  params.delete("action");
  params.sort();
  return `${path.replace(/\/+$/, "")}?${params}`;
};

const labelOf = (row) => row?.displayName || row?.name || "";

// Every sidebar page: { title, path, route }.
const sidebarPages = (routes) =>
  searchProducts(routes).flatMap((product) =>
    (product.collapse || [])
      .filter((section) => !section?.hideInSidebar)
      .flatMap((section) => {
        const children = (section.collapse || []).filter((row) => !row?.hideInSidebar);
        if (!children.length) {
          return [{ title: labelOf(section), path: labelOf(product), route: section.path }];
        }
        return children.map((row) => ({
          title: labelOf(row),
          path: `${labelOf(product)} › ${labelOf(section)}`,
          route: row.path,
        }));
      }),
  );

// Spec 4.4 ranking: 1 title starts with the text, 2 a word in the title
// starts with it, 3 the path (product/section) starts with it at a word,
// 4 the title contains it. null = no match.
const rank = (page, needle) => {
  const title = normalize(page.title);
  if (title.startsWith(needle)) return 1;
  const words = title.split(" ");
  if (words.some((_, index) => words.slice(index).join(" ").startsWith(needle))) return 2;
  const pathWords = normalize(page.path).split(" ");
  if (pathWords.some((_, index) => pathWords.slice(index).join(" ").startsWith(needle))) return 3;
  if (title.includes(needle)) return 4;
  return null;
};

const highlightIn = (title, query) => {
  const raw = String(query).trim().toLowerCase().replace(/\s+/g, " ");
  const at = String(title).toLowerCase().indexOf(raw);
  return at >= 0 ? [[at, at + raw.length]] : [];
};

// Server pages first, then the user's own sidebar pages that match and the
// server didn't return, up to `limit`. `more` is true when matches were left out.
export const withLocalPages = ({ serverPages, query, routes, accessByProduct, context, limit }) => {
  const needle = normalize(query);
  const seen = new Set((serverPages || []).map((page) => routeKey(page.route)));
  const local = needle
    ? sidebarPages(routes)
        .map((page) => ({ page, rank: rank(page, needle) }))
        .filter(({ page, rank: r }) => r != null && !seen.has(routeKey(page.route)))
        .filter(({ page }) => canOpenRoute(routes, accessByProduct, context, page.route) === true)
        .sort((a, b) => a.rank - b.rank || a.page.title.localeCompare(b.page.title))
        .filter(({ page }) => {
          const key = routeKey(page.route);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map(({ page }) => ({
          id: `local:${routeKey(page.route)}`,
          type: "page",
          title: page.title,
          path: page.path,
          route: page.route,
          highlight: highlightIn(page.title, query),
        }))
    : [];
  const all = [...(serverPages || []), ...local];
  return { pages: all.slice(0, limit), more: all.length > limit };
};

// The sidebar page on screen (same path, and the query when the row has
// one), as a Recent entry; null when the URL isn't a sidebar page.
export const sidebarPageFor = (routes, pathname, search) => {
  const match = findSidebarRow(routes, `${pathname}${search || ""}`);
  if (!match || match.score < 1000) return null;
  const { product, section, row } = match;
  return {
    id: `local:${routeKey(row.path)}`,
    type: "page",
    title: labelOf(row),
    path: row === section ? labelOf(product) : `${labelOf(product)} › ${labelOf(section)}`,
    route: row.path,
  };
};
